import angular from 'angular';
import * as R from 'ramda';
import userService from '../membership/ss.user.service';
import ISharedModelService from './ISharedModel.service';
import searchVendors from './vendors.search.filter';
import vmDataService from '../data/vm.data.service';


let _userService;
let _vmDataService;
let _searchVendorsFilter;
let _serviceIds;
let _unfilteredVendors = [];

export class vmSharedModelService extends ISharedModelService {
  constructor(
    $q,
    $filter,
    userService, // eslint-disable-line no-shadow
    searchVendorsFilter,
    serviceIds,
    vmDataService // eslint-disable-line no-shadow
  ) {
    super('vm');

    /* Private Members */
    _userService = userService;
    _vmDataService = vmDataService;
    _searchVendorsFilter = searchVendorsFilter;
    _serviceIds = serviceIds;

    /* Public Properties */
    /*
     * NOTE: The "legacy" properties below support the strategy
     * that clients access this service's properties directly, and must
     * watch the "dfdRefresh" promise to know when the properties
     * have values. This can result in accidental race conditions, hiding errors
     * in the original $http promise, and extra code complexity.
     *
     * The new way is for clients to not use these properties at all.
     * Instead, "smart components" (top level components) call these methods and get
     * the data they need in the .then() and put the resolved data into their scopes.
     * "Dumb components" should continue to receive scope properties through their attributes.
     *
     * Clients should avoid mutating the data received from these functions if at all possible.
     * Instead, look to see if you can add a function here that returns the changed state.
     * This service should end up having many getters and setters.
     */
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.dfdRefreshSettings = $q.defer();
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.dfdRefreshMasterVendors = $q.defer();
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.dfdRefreshVendors = $q.defer();
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.dfdRefreshRiskTiers = $q.defer();
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.vmSettings = null;
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.masterVendors = null;
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.vendors = null;
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.riskTiers = null;
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.filters = _getDefaultFilters();

    console.log('app.services.vmSharedModelService created');
  }


  /**
   * Returns a cached promise, or fetches the settings data
   * @public
   * @param {Boolean} forceRefresh - True if api call must occur, default is false
   * @async
   * @returns {Promise<Array>}
   */
  getSettings(forceRefresh = false) {
    if (forceRefresh || !this.cache.settings) {
      this.cache.settings = _userService.getUser()
        .then(userData => _vmDataService.getSettings(userData.customerId)
          .then((response) => {
            const settings = response.data.result[0];

            this.vmSettings = settings; // legacy
            this.dfdRefreshSettings.resolve();// legacy
            return settings;
          }));
    }
    return this.cache.settings;
  }

  /**
   * Returns a cached promise, or fetches the Master Vendors data
   * @public
   * @async
   * @param {Boolean} forceRefresh - True if api call must occur, default is false
   * @returns {Promise<Array>}
   */
  getMasterVendors(forceRefresh = false) {
    if (forceRefresh || !this.cache.masterVendors) {
      this.cache.masterVendors = _vmDataService.getMasterVendors()
        .then((response) => {
          const masterVendors = response.data.result;

          this.masterVendors = masterVendors; // legacy
          this.dfdRefreshMasterVendors.resolve();// legacy
          return masterVendors;
        });
    }
    return this.cache.masterVendors;
  }

  /**
   * Returns a cached promise, or fetches the unused Master Vendors data
   * NOTE: we assume that this is the compliance app Vendor Management for the serviceId
   * @public
   * @async
   * @param {int} [currentMasterVendorId] - the master vendor currently being used
   * @returns {Promise<Array>}
   */
  getUnusedMasterVendors(currentMasterVendorId = -1) {
    const compareFn = (masterVendor, vendor) => masterVendor.masterVendorId === vendor.masterVendorId
      && masterVendor.masterVendorId !== currentMasterVendorId;
    return this.getMasterVendors()
    // NOTE: we assume that this is the compliance app Vendor Management for the serviceId
      .then(masterVendors => this.getVendors(_serviceIds.vendorManagement)
        .then(vendors => R.differenceWith(compareFn, masterVendors, vendors)));
  }

  /**
   * Returns a cached promise, or fetches the vendor data. Gets a detailed vendor model.
   * @public
   * @async
   * @param {int} vendorId - the vendor's id
   * @param {Boolean} forceRefresh - True if api call must occur, default is false
   * @returns {Promise<vendor>}
   */
  getVendor(vendorId, forceRefresh = false) {
    const key = vendorId.toString();

    if (forceRefresh || !this.cache.vendor[key]) {
      this.cache.vendor[key] = _vmDataService.getVendor(vendorId)
        .then(response => response.data);
    }
    return this.cache.vendor[key];
  }

  /**
   * Returns a cached promise, or fetches the vendor data. Gets a detailed vendor model.
   * @public
   * @async
   * @param {int} vendorId - the vendor's id
   * @param {Boolean} forceRefresh - True if api call must occur, default is false
   * @returns {Promise<vendor>}
   */
  getVendorDetails(vendorId, forceRefresh = false) {
    const key = vendorId.toString();

    if (forceRefresh || !this.cache.vendor[key]) {
      this.cache.vendor[key] = _vmDataService.getVendorDetails(vendorId)
        .then(response => response.data);
    }
    return this.cache.vendor[key];
  }

  saveVendor(vendor) {
    return _vmDataService.saveVendor(vendor, true)
      .then((response) => {
        const savedVendor = response.data;
        this.cache.invalidate('vendors');
        this.cache.saveToHashedPromise('vendor', savedVendor, savedVendor.vendorId);
        return savedVendor;
      });
  }

  deleteVendor(vendorId) {
    return _vmDataService.deleteVendor(vendorId).then(() => {
      this.cache.invalidate('vendors');
      this.cache.invalidate('vendor', vendorId);
    });
  }

  /**
   * Returns a cached promise, or fetches the vendor data. Vendor models are abbreviated. Use getVendor for details.
   * @public
   * @async
   * @param {Boolean} forceRefresh - True if api call must occur, default is false
   * @returns {Promise<Array>}
   */
  getVendors(serviceId, forceRefresh = false) {
    const pageNumber = 1;
    const pageSize = 2000;
    const statusId = 1;
    const returnCount = true;

    if (forceRefresh || !this.cache.vendors) {
      this.cache.vendors = _vmDataService
        .getVendors(serviceId, pageNumber, pageSize, statusId, returnCount)
        .then((response) => {
          const vendors = response.data.result;

          _unfilteredVendors = vendors; // legacy
          this.dfdRefreshVendors.resolve();// legacy
          return this.filterVendors(vendors);
        });
    }
    return this.cache.vendors;
  }

  /**
   * Forces an api call to fetch Vendors
   * NOTE: we assume that this is the compliance app Vendor Management for the serviceId
   * @public
   * @async
   * @returns {Promise<Array>}
   */
  refreshVendors() {
    // NOTE: we assume that this is the compliance app Vendor Management for the serviceId
    return this.getVendors(_serviceIds.vendorManagement, true);
  }

  /**
   * Create a new vendor with default values
   * @param {Object} [additionalProps] - key/value pairs with more default values
   * @returns {{vendorId: number, name: null}}
   */
  createNewVendor(additionalProps) {
    return {
      vendorId: -1,
      name: null,
      countryId: 1,
      ...additionalProps
    };
  }

  /**
   * Returns a cached promise, or fetches the data
   * @public
   * @async
   * @param vendorId
   * @param name
   * @returns {Promise<boolean>} - Indicates if the name already exist
   */
  checkVendorNameExists(vendorId, name) {
    const key = `${vendorId}${this.getHash(name)}`;
    if (!this.cache.vendorName[key]) {
      this.cache.vendorName[key] = _vmDataService.checkVendorExists(vendorId, name)
        .then(response => response.data.result);
    }
    return this.cache.vendorName[key];
  }

  /**
   * Returns a cached promise, or fetches the data
   * @public
   * @async
   * @param vendorId
   * @param productId
   * @param name
   * @returns {Promise<boolean>} - Indicates if the name already exist
   */
  checkProductNameExists(vendorId, productId, name) {
    const key = `${vendorId}${productId}${this.getHash(name)}`;
    if (!this.cache.productName[key]) {
      this.cache.productName[key] = _vmDataService.checkProductExists(vendorId, productId, name)
        .then(response => response.data.result);
    }
    return this.cache.productName[key];
  }

  /**
   * Returns a cached promise, or fetches the Master Vendors data
   * @public
   * @async
   * @param {int} masterVendorId - The master vendor which contains master products
   * @param {Boolean} forceRefresh - True if api call must occur, default is false
   * @returns {Promise<Array>}
   */
  getMasterProducts(masterVendorId, forceRefresh = false) {
    const key = masterVendorId.toString();
    if (forceRefresh || !this.cache.masterProduct[key]) {
      this.cache.masterProduct[key] = _vmDataService.getMasterProducts(masterVendorId)
        .then(response => response.data.result);
    }
    return this.cache.masterProduct[key];
  }

  /**
   * Returns a cached promise, or fetches a vendor's products
   * @public
   * @async
   * @param {int} vendorId - The vendor which contains products
   * @param {Boolean} forceRefresh - True if api call must occur, default is false
   * @returns {Promise<Array>} - The collection of products for the vendor
   */
  getProducts(vendorId, forceRefresh = false) {
    const key = vendorId.toString();
    if (forceRefresh || !this.cache.product[key]) {
      this.cache.product[key] = _vmDataService.getProducts(vendorId)
        .then(response => response.data.result);
    }
    return this.cache.product[key];
  }

  resetProductCache(vendorId) {
    const key = vendorId.toString();
    if (this.cache.product[key]) {
      // R.remove(R.indexOf(key, this.cache.product), 1, this.cache.product);
      this.cache.invalidate('product', key);
    }
  }

  saveProduct(product) {
    return _vmDataService.saveProduct(product, true)
      .then((response) => {
        const savedProduct = response.data;
        // this.cache.invalidate('product');
        this.resetProductCache(product.vendorId);
        return savedProduct;
      });
  }

  deleteProduct(productId) {
    return _vmDataService.deleteProduct(productId).then(() => {
      this.cache.invalidate('product');
    });
  }

  /**
   * Returns a cached promise, or fetches a master vendor's products
   * @public
   * @async
   * @param {int} masterVendorId - The master vendor which contains master products
   * @param {int} vendorId - The vendor to check for used master products
   * @param {int} [currentMasterProductId] - the master product currently being used
   * @returns {Promise<Array>} - The collection of master products for the master vendor
   */
  getUnusedMasterProducts(masterVendorId, vendorId, currentMasterProductId = -1) {
    if ((masterVendorId === 0 || masterVendorId === -1 || masterVendorId === null)) {
      return Promise.resolve([]);
    }
    const compareFn = (masterProduct, product) => masterProduct.masterProductId === product.masterProductId
      && masterProduct.masterProductId !== currentMasterProductId;
    return this.getMasterProducts(masterVendorId)
      .then(masterProducts => this.getProducts(vendorId)
        .then(products => R.differenceWith(compareFn, masterProducts, products)));
  }

  /**
   * Returns a cached promise, or fetches the Risk Tiers data
   * @public
   * @async
   * @param {Boolean} forceRefresh - True if api call must occur, default is false
   * @returns {Promise<Array>}
   */
  getRiskTiers(forceRefresh = false) {
    if (forceRefresh || !this.cache.riskTiers) {
      this.cache.riskTiers = _userService.getUser()
        .then(userData => _vmDataService.getRiskTierInformation(userData.customerId)
          .then((response) => {
            const riskTiers = response.data.result;

            this.riskTiers = riskTiers; // legacy
            this.dfdRefreshRiskTiers.resolve();// legacy
            return riskTiers;
          }));
    }
    return this.cache.riskTiers;
  }

  getFilters() {
    return this.filters;
  }

  /**
   * Updates an internal copy and returns the results
   * @public
   * @param unfilteredVendors - Optional parameter
   * @returns {vendors}
   */
  filterVendors(unfilteredVendors = _unfilteredVendors) {
    const filteredVendors = _searchVendorsFilter(unfilteredVendors, this.filters);

    this.vendors = filteredVendors; // legacy
    this._setVendorProductIsExpandedByRef(filteredVendors);
    return filteredVendors;
  }

  /**
   * Resets the internal copy of filters
   * @public
   * @returns undefined
   */
  clearFilters() {
    this.filters = _getDefaultFilters();
    return this.filters;
  }

  /**
   * Wing open the vendor if the search string matches its product
   * Mutates the original array by reference
   * @private
   * @returns undefined
   */
  _setVendorProductIsExpandedByRef(vendors) {
    if (vendors.length > 0) {
      vendors.forEach((vendor) => {
        if (R.is(Array, vendor.products) && vendor.products.length > 0) {
          let isProductNameMatch = false;

          vendor.products.forEach((product) => {
            isProductNameMatch = _search(product.name, this.filters.searchText);
            if (isProductNameMatch) {
              vendor.isExpanded = true;
              return false;
            }
          });
          if (!isProductNameMatch) {
            vendor.isExpanded = false;
          }
        }
      });
    }
  }

  /**
   * Initializes Vendor Management
   * @public
   * @async
   * @param
   * @returns
   */
  initVendorManagement(serviceId) {
    return _vmDataService.initVendorManagement(serviceId);
  }
}

vmSharedModelService.$inject = [
  '$q',
  '$filter',
  'userService',
  'searchVendorsFilter',
  'serviceIds',
  'vmDataService'
];

export default angular.module('app.services.vmSharedModelService', [
  userService,
  vmDataService,
])
  .service('vmSharedModelService', vmSharedModelService)
  .filter('searchVendors', () => searchVendors)
  .name;


/** *************************************************************
 * Private Functions
 */

function _search(stringToSearch, searchText) {
  if (searchText.length === 0) {
    return false;
  }
  return stringToSearch.toLocaleLowerCase()
    .indexOf(searchText.toLocaleLowerCase()) > -1;
}

function _getDefaultFilters() {
  return {
    searchText: '',
    inherentRiskNone: true,
    inherentRiskLow: true,
    inherentRiskMedium: true,
    inherentRiskHigh: true,
    residualRiskNone: true,
    residualRiskLow: true,
    residualRiskMedium: true,
    residualRiskHigh: true,
    showAlerts: true,
    showReminders: true,
    showNone: true,
    selectedVendorManagerId: -1,
  };
}
